import { useCallback, useState, useMemo } from 'react';
import FileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import ServiceManager from 'services/ServiceManager';
import AdapterError from 'errors/AdapterError';
import handlerRequestCanceling from 'utils/handlerRequestCanceling';
import HandlerError from 'errors/HandlerError';
import { useLanguageId } from 'hooks/useLanguageId';
import { formatDate } from 'utils/datetime';

// --- Interfaces ---

interface TemplateResponseData {
    Base64Content: string;
}

interface FileData {
    stringFile: string;
    fileType: string;
}

interface DownloadReportResult {
    downloadReport: () => void;
    error: any; // Ideally replace with a specific Error type
    isLoading: boolean;
    fileName: string;
}

// --- Helper Functions ---

export const file = (data: TemplateResponseData): FileData => ({
    stringFile: atob(data.Base64Content),
    fileType: 'application/pdf', // Using standard MIME type
});

export const useDownloadReport = (
    contactId: string | number,
    contactName: string,
): DownloadReportResult => {
    const [error, setError] = useState<any>(null);
    const [isLoading, setLoading] = useState<boolean>(false);
    const { t, i18n } = useTranslation();
    const { getLanguageId } = useLanguageId();

    const fileName = useMemo(
        () => `${t('clientDashboard.overview.investmentOverview')} - ${contactName} - ${formatDate()}.pdf`,
        [contactName, t],
    );

    const downloadReport = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // 1. Get Language Info
            const language = await getLanguageId();

            const params = {
                TemplateMetadataKey: 'InvestmentOverview',
                LanguageId: language?.Id,
                ContentType: 'application/pdf',
                FallbackLanguageId: 2,
                Parameters: {
                    ContactId: contactId,
                    TimeFrame: 'm',
                    LanguageCode: i18n.language,
                },
                IncludePlainProperties: false,
                PageOrientation: 'Landscape',
            };

            // 2. Request Template Generation
            const response = await ServiceManager.commonContent('generateTemplates', [params]);

            // 3. Process Base64 to Blob
            try {
                const { stringFile, fileType } = file(response.data);
                const byteLength = stringFile.length;
                const buffer = new ArrayBuffer(byteLength);
                const dataBuffer = new Uint8Array(buffer);

                for (let i = 0; i < byteLength; i += 1) {
                    dataBuffer[i] = stringFile.charCodeAt(i);
                }

                const newBlob = new Blob([buffer], { type: fileType });

                // 4. Trigger Download
                FileSaver.saveAs(newBlob, fileName);
            } catch (err: any) {
                throw new AdapterError(err);
            }
        } catch (err: any) {
            // We use the existing error handlers
            const handleError = handlerRequestCanceling(
                HandlerError({ setLoading, setError }),
            );

            handleError(err);
        } finally {
            setLoading(false);
        }
    }, [contactId, contactName, fileName, getLanguageId, i18n.language]);

    return {
        downloadReport,
        error,
        isLoading,
        fileName,
    };
};
